// Contact screening (SER-5741): its own `contact_screening` wire component.
// The palette node edits the screening fields and serialization ships the lean
// config as-is (context, ask_permission, initial_wait_time; channel locked to
// call) — contacto-PHLO-core owns the disposition/instructions contract and
// emits the runtime `screening_task` payload. Disposition ids double as port
// ids, edge keys, and intent names, matching the component's output states.

import { VOICEMAIL_DETECTED_INTENT } from '../constants'
import { FlowNodeTypes } from '../types'

// Builder node types that ARE a call-channel AI screening node — the composite
// contains one. Shared by every graph walk that must treat screening as an AI
// call node (first-level checks, End-node call-connectivity, etc.).
export const SCREENING_NODE_TYPES: ReadonlySet<string> = new Set([
  FlowNodeTypes.contact_screening,
  FlowNodeTypes.outbound_screening,
])

// Builder node types that place an outbound call. The outbound screening
// composite embeds an initiate_call half until serialization, so every
// builder-graph "is a call active / does this flow dial" check must count it.
export const CALL_PLACING_NODE_TYPES: ReadonlySet<string> = new Set([
  FlowNodeTypes.initiate_call,
  FlowNodeTypes.outbound_screening,
])

export interface ScreeningDisposition {
  id: string
  name: string
  // Customer-facing one-liner (port-row + edge-chip hover). Distinct from
  // intent_instructions, which is LLM-facing classification prose.
  description: string
  intent_instructions: string
}

// Ids mirror the runtime's status→disposition bucketing in
// plivo-cx-livekit/src/units/tasks/contact_screening.py — do not rename.
export const SCREENING_DISPOSITIONS: ScreeningDisposition[] = [
  {
    id: 'reached',
    description: "The right person answered and confirmed it's them.",
    name: 'Contact reached',
    intent_instructions:
      'The intended contact was reached and is willing to continue the conversation.',
  },
  {
    id: 'callback',
    description: 'They asked to be called back later.',
    name: 'Callback requested',
    intent_instructions: 'The caller asked for this call to happen at another time.',
  },
  {
    id: 'wrong_contact',
    description: 'Someone else answered, or the number is wrong.',
    name: 'Wrong contact',
    intent_instructions:
      'The person or number reached is not the intended contact and cannot hand over the call.',
  },
  {
    id: 'unavailable',
    description: "The contact couldn't come to the phone.",
    name: 'Contact unavailable',
    intent_instructions:
      'The intended contact is not available and no callback time could be established.',
  },
  {
    id: 'declined',
    description: "They answered but didn't want to continue.",
    name: 'Declined to continue',
    intent_instructions: 'A person was reached but declined to continue the conversation now.',
  },
  {
    id: 'do_not_call',
    description: 'They asked not to be contacted again.',
    name: 'Do not call again',
    intent_instructions: 'The person explicitly asked not to be contacted again.',
  },
]

// Screening is outbound-only, so voicemail detection is always on — same as
// the auto-added voicemail intent on outbound ai_agent_v2 nodes. The port id
// is the intent name the runtime fires on AMD detection.
export const SCREENING_VOICEMAIL_PORT = {
  default: true,
  id: VOICEMAIL_DETECTED_INTENT.name,
  name: 'Voicemail',
  description: 'The call went to voicemail.',
  section: 'Unanswered',
}

// Fixed outcome ports of a screening node; ids double as wire intent names
// and edge keys. Shared by the standalone node and the outbound composite.
export const SCREENING_OUTCOME_PORTS = [
  ...SCREENING_DISPOSITIONS.map(({ id, name, description }) => ({
    default: true,
    id,
    name,
    description,
    section: 'Answered',
  })),
  SCREENING_VOICEMAIL_PORT,
]

export interface ContactScreeningConfig {
  name: string
  description?: string | null
  context: string
  ask_permission: boolean
  initial_wait_time?: number
}

// Node variables recorded by the screening runtime
// (plivo-cx-livekit ScreeningUnit._record_outputs).
export const SCREENING_NODE_VARS = [
  'screening_disposition',
  'screening_status',
  'screening_answered_by',
  'screening_relationship',
  'screening_callback_time',
]
